import { VenueRequest } from '../components/ContributionCoverage';
import { SeatPreviewSheet } from '../components/SeatPreviewSheet';
import { BottomSheet } from '../components/BottomSheet';
import { seatFacts, type SeatLayoutSection } from '@anjabom/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  candidateId,
  categories,
  searchVenues,
  seatLayoutFor,
  resolveLayoutSeat,
  sameSeatVisit,
  reviewSeat,
  venueById,
  type ImplementedCategoryId,
  type SeatCandidate,
  type Venue,
  type VenueSpace,
} from '@anjabom/core';
import { Breadcrumb } from '../components/Breadcrumb';
import { InteractiveSeatMap } from '../components/InteractiveSeatMap';
import { SurfaceBanner } from '../components/SurfaceBanner';
import { track } from '../lib/analytics';
import { loadVenueSearch, venueQueryTooLong, type VenueSearchResult } from '../lib/venue-search';
import { readSurfaceState } from '../lib/surface-state';
import { useLocalState } from '../state/LocalStateProvider';
import '../styles/entry-flow.css';

type Step = 1 | 2 | 3 | 4 | 5;

export function StartPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { state, storageProblem, addCandidate, retrySave, removeCandidate, rememberVenue } = useLocalState();
  const venueParam = params.get('venue');
  const recordIntent = params.get('intent') === 'record';
  const initialVenue = venueParam ? venueById(venueParam) ?? null : null;
  const [directVenueEntry, setDirectVenueEntry] = useState(Boolean(initialVenue));
  const [step, setStep] = useState<Step>(initialVenue && params.get('space') ? 5 : initialVenue ? 3 : 1);
  const [categoryId, setCategoryId] = useState<ImplementedCategoryId | null>(initialVenue?.categoryId ?? null);
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [venue, setVenue] = useState<Venue | null>(initialVenue);
  const [space, setSpace] = useState<VenueSpace | null>(initialVenue?.spaces.find((s)=>s.id===params.get('space'))??null);
  const [customSpace, setCustomSpace] = useState('');
  const [instanceTitle, setInstanceTitle] = useState('');
  const [instanceAt, setInstanceAt] = useState('');
  const [skipInstance, setSkipInstance] = useState(true);
  const [preview, setPreview] = useState<{section: string; row: string; number: number} | null>(null);
  const [sectionPreview, setSectionPreview] = useState<SeatLayoutSection | null>(null);
  const [sectionCode, setSectionCode] = useState(params.get('section')??'');
  const [rowLabel, setRowLabel] = useState(params.get('row')??'');
  const [seatNumber, setSeatNumber] = useState(params.get('number')??'');
  const [expectedPrice, setExpectedPrice] = useState('');
  const [message, setMessage] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [selectedFromMap, setSelectedFromMap] = useState(false);
  const [currentFlowCandidateIds, setCurrentFlowCandidateIds] = useState<string[]>([]);
  const [searchResult, setSearchResult] = useState<{ category: ImplementedCategoryId; query: string; result: VenueSearchResult } | null>(null);
  const [searchRetry, setSearchRetry] = useState(0);
  const [surface] = useState(readSurfaceState);
  const reportedLayoutCandidateSets = useRef(new Set<string>());
  const progressStep = directVenueEntry ? (step === 5 ? 2 : 1) : (step === 5 ? 4 : Math.min(step, 4));
  const progressTotal = directVenueEntry ? 2 : 4;

  useEffect(() => {
    if (!categoryId) return;
    const controller = new AbortController();
    void loadVenueSearch(categoryId, query, controller.signal).then((result) => {
      if (!controller.signal.aborted) setSearchResult({ category: categoryId, query, result });
    }).catch(() => { /* Aborted requests cannot replace the current search. */ });
    return () => controller.abort();
  }, [categoryId, query, searchRetry]);

  const currentSearch = searchResult?.category === categoryId && searchResult.query === query ? searchResult.result : null;
  const invalidQuery = venueQueryTooLong(query) || currentSearch?.status === 'invalid';
  const venues = useMemo(() => invalidQuery ? [] : currentSearch?.status === 'ready' ? currentSearch.venues : (categoryId ? searchVenues(categoryId, query) : []), [categoryId, query, currentSearch, invalidQuery]);
  const resolvedSpaceName = customSpace.trim() || space?.name || '';
  const currentFlowCandidates = state.candidates.filter((candidate) => currentFlowCandidateIds.includes(candidate.candidateId));
  const previousCandidates = state.candidates.filter((candidate) => !currentFlowCandidateIds.includes(candidate.candidateId));
  const seatLayout = useMemo(
    () => venue && space ? seatLayoutFor(venue.id, space.id) : undefined,
    [space, venue],
  );
  const canContinue = step === 1 ? Boolean(categoryId)
    : step === 2 ? Boolean(venue)
      : step === 3 ? Boolean(resolvedSpaceName)
        : step === 4 ? skipInstance || Boolean(instanceTitle.trim() || instanceAt)
        : step === 5 ? currentFlowCandidates.length > 0
            : true;

  const chooseCategory = (id: ImplementedCategoryId) => {
    setCurrentFlowCandidateIds([]);
    setDirectVenueEntry(false);
    setCategoryId(id);
    setVenue(null);
    setSpace(null);
    setSelectedFromMap(false);
    setSearchResult(null);
    setStep(2);
    track('category_selected');
  };
  const chooseVenue = (next: Venue) => {
    setCurrentFlowCandidateIds([]);
    setDirectVenueEntry(false);
    setVenue(next);
    rememberVenue(next.id);
    setSpace(null);
    setSelectedFromMap(false);
    setStep(3);
    track('venue_selected');
  };
  const chooseSpace = (next: VenueSpace) => {
    setCurrentFlowCandidateIds([]);
    setSpace(next);
    setCustomSpace('');
    setSectionCode(next.sectionSuggestions[0] ?? '');
    setRowLabel('');
    setSeatNumber('');
    setSelectedFromMap(false);
    setStep(5);
  };

  const addSeat = (openResult = false) => {
    setMessage('');
    setPreviewError('');
    const fail = (reason: string) => { setMessage(reason); setPreviewError(reason); };
    const number = Number(seatNumber);
    if (!venue || !categoryId || !resolvedSpaceName || !sectionCode.trim() || !rowLabel.trim() || !Number.isInteger(number) || number < 1 || number > 9999) {
      fail('구역, 열, 좌석번호를 티켓에 적힌 그대로 확인해 주세요.');
      return;
    }
    const coordinates = seatLayout ? resolveLayoutSeat(seatLayout, { sectionCode, rowLabel, seatNumber: number }) : null;
    if (seatLayout && !coordinates) {
      fail('이 홀의 공식 배치에서 구역·열·번호를 확인하지 못했어요. 아래 좌석도에서 고르거나 티켓 표기를 다시 확인해 주세요.');
      return;
    }
    const candidate: SeatCandidate = {
      candidateId: candidateId(),
      categoryId,
      venueId: venue.id,
      venueName: venue.name,
      spaceId: space?.id ?? `custom-${resolvedSpaceName}`,
      spaceName: resolvedSpaceName,
      layoutVersionId: coordinates && seatLayout
        ? seatLayout.layoutVersionId
        : `LV-${venue.id}-${space?.id ?? 'unverified'}-unconfirmed`,
      instanceId: skipInstance ? null : instanceTitle.trim() || instanceAt || null,
      instanceLabel: skipInstance ? null : [instanceTitle.trim(), instanceAt].filter(Boolean).join(' · ') || null,
      sectionCode: coordinates?.sectionCode ?? sectionCode.trim(),
      rowLabel: coordinates?.rowLabel ?? rowLabel.trim(),
      seatNumber: number,
      expectedPrice: expectedPrice ? Number(expectedPrice) : null,
    };
    const existing = state.candidates.find((seat) => sameSeatVisit(seat, candidate));
    if (!existing && state.candidates.length >= 4) {
      fail('후보 네 자리가 이미 있어요. 시트를 닫고 필요 없는 후보를 빼면 이 자리를 담을 수 있어요. 개인 기록은 지워지지 않아요.');
      return;
    }
    const savedCandidate = existing ?? candidate;
    if ((!existing && !addCandidate(candidate)) || (existing && storageProblem && !retrySave())) {
      fail('이 자리를 아직 기기에 저장하지 못했어요. 입력은 그대로예요. 저장 공간을 확인한 뒤 같은 버튼으로 다시 시도해 주세요.');
      return;
    }
    setCurrentFlowCandidateIds((ids) => ids.includes(savedCandidate.candidateId) ? ids : [...ids, savedCandidate.candidateId]);
    if (openResult) {
      track('candidate_added');
      track('layout_version_selected', {
        stageConfig: selectedFromMap ? 'known_layout' : 'unconfirmed_layout',
        source: selectedFromMap ? 'interactive_map' : 'manual_coordinates',
      });
      navigate(recordIntent ? `/review/new?candidate=${savedCandidate.candidateId}` : `/seat/${savedCandidate.candidateId}`);
      return;
    }
    setSeatNumber('');
    setExpectedPrice('');
    setSelectedFromMap(false);
    setMessage(existing ? '이미 담은 자리예요. 같은 후보를 다시 쓰고 있어요.' : '후보에 담았어요. 다른 자리도 담으면 함께 비교할 수 있어요.');
    track('candidate_added');
  };

  const next = () => {
    if (step === 5 && canContinue) {
      const decisionCandidates = currentFlowCandidates;
      const layoutKey = decisionCandidates
        .map((candidate) => candidate.layoutVersionId)
        .sort()
        .join('|');
      if (!reportedLayoutCandidateSets.current.has(layoutKey)) {
        reportedLayoutCandidateSets.current.add(layoutKey);
        const usesKnownLayout = decisionCandidates.some((candidate) => !candidate.layoutVersionId.endsWith('-unconfirmed'));
        track('layout_version_selected', {
          stageConfig: usesKnownLayout ? 'known_layout' : 'unconfirmed_layout',
          source: usesKnownLayout ? 'interactive_map' : 'manual_coordinates',
        });
      }
      const only = currentFlowCandidates[0];
      if (currentFlowCandidates.length === 1 && only) navigate(`/seat/${only.candidateId}`);
      else navigate('/compare', { state: { candidateIds: currentFlowCandidates.map((candidate) => candidate.candidateId) } });
      return;
    }
    if (step < 5 && canContinue) setStep(step === 3 ? 5 : (step + 1) as Step);
  };

  return (
    <section className="page narrow entry-flow-page">
      <Breadcrumb items={[{ label: '홈', href: '/' }, { label: '좌석 선택' }]} />
      <SurfaceBanner state={surface} empty="아직 등록되지 않은 곳이에요." />
      <div className="wizard-head">
        <div>
          <p className="eyebrow">로그인 없이 바로 비교해요</p>
          <h1>{stepTitles[step]}</h1>
        </div>
        <span className="micro">{progressStep} / {progressTotal}</span>
      </div>
      <ol className={`stepper steps-${progressTotal}`} aria-label="선택 진행률">
        {Array.from({ length: progressTotal }, (_, index) => index + 1).map((item) => <li key={item} data-active={item <= progressStep} aria-label={`${item}단계`} />)}
      </ol>

      <section className="selection-summary" aria-label="현재 선택">
        {categoryId ? <button type="button" className="summary-chip" onClick={() => { setDirectVenueEntry(false); setStep(1); }}><strong>{categoryId === 'performance' ? '공연·콘서트' : '영화'}</strong> 바꾸기</button> : null}
        {venue ? <button type="button" className="summary-chip" onClick={() => { setDirectVenueEntry(false); setStep(2); }}><strong>{venue.name}</strong> 바꾸기</button> : null}
        {resolvedSpaceName ? <button type="button" className="summary-chip" onClick={() => setStep(3)}><strong>{resolvedSpaceName}</strong> 바꾸기</button> : null}
        {(instanceTitle || instanceAt) ? <span className="summary-chip"><strong>{instanceTitle || instanceAt}</strong> 내가 입력한 회차</span> : null}
      </section>

      {step === 1 ? (
        <div className="stack-lg">
          <p className="muted">지금은 공연·콘서트와 영화를 정확하게 다뤄요. 다른 지정석은 배치가 검증된 뒤 열어요.</p>
          <div className="choice-grid">
            {categories.filter((category) => category.implemented).map((category) => (
              <button
                key={category.id}
                type="button"
                className="choice-card"
                disabled={!category.implemented}
                aria-pressed={category.id === categoryId}
                onClick={() => category.implemented && chooseCategory(category.id as ImplementedCategoryId)}
              >
                {!category.implemented ? <span className="soon">준비 중</span> : null}
                <span className="choice-title">{category.label}</span>
                <span className="choice-description">{category.description}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {step === 2 && categoryId ? (
        <div className="stack-lg">
          <label className="field">
            <span className="field-label">장소 이름이나 지역</span>
            <input className="input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={categoryId === 'performance' ? '예: 블루스퀘어, 광화문' : '예: 용산 CGV, 잠실'} />
          </label>
          <div className="venue-list" aria-live="polite">
            {invalidQuery ? <p role="alert">{venueQueryTooLong(query) ? '장소 이름이나 지역을 100자 이내로 줄여 다시 검색해 주세요.' : '검색어를 확인하고 고쳐 다시 검색해 주세요.'}</p> : null}
            {!invalidQuery && currentSearch?.status === 'unavailable' ? <p role="status">검색을 불러오지 못해 이 기기에 있는 장소만 보여요. <button type="button" onClick={() => setSearchRetry((value) => value + 1)}>다시 검색하기</button></p> : null}
            {venues.map((item) => (
              <button key={item.id} className="venue-card" type="button" aria-pressed={venue?.id === item.id} onClick={() => chooseVenue(item)}>
                <span><span className="venue-name">{item.name}</span><br /><span className="venue-meta">{item.region} · {item.addressHint}</span></span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
            {!invalidQuery && currentSearch?.status === 'ready' && venues.length === 0 ? (
              <div className="card">
                <h3>지원하는 장소를 찾지 못했어요.</h3><VenueRequest/>
                <p className="muted">공연명이나 영화명을 입력했다면 티켓에 적힌 공연장·영화관 이름으로 다시 확인해 주세요.</p>
                <div className="inline">
                  <Link className="button button-primary" to="/explore">지원 장소에서 고르기</Link>
                  <Link className="button" to="/support">장소 등록 요청하기</Link>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {step === 3 && venue ? (
        <div className="stack-lg">
          <p className="muted">티켓에 적힌 홀이나 상영관을 골라 주세요. 번호 상영관은 직접 입력할 수 있어요.</p>
          <div className="venue-list">
            {venue.spaces.map((item) => (
              <button key={item.id} className="venue-card" type="button" aria-pressed={space?.id === item.id} onClick={() => chooseSpace(item)}>
                <span><span className="venue-name">{item.name}</span>{item.format ? <><br /><span className="venue-meta">{item.format}</span></> : null}</span>
                <span aria-hidden="true">›</span>
              </button>
            ))}
          </div>
          <label className="field">
            <span className="field-label">목록에 없으면 직접 입력해요</span>
            <input className="input" value={customSpace} onChange={(event) => { setCurrentFlowCandidateIds([]); setCustomSpace(event.target.value); setSpace(null); }} placeholder={categoryId === 'cinema' ? '예: 7관' : '예: 오페라극장'} />
          </label>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="stack-lg">
          <div className="notice"><span className="notice-mark">!</span><span>회차나 상영 포맷을 넣으면 같은 좌석이라도 다른 무대 구성을 분리할 수 있어요.</span></div>
          <label className="field">
            <span className="field-label">공연명이나 영화명</span>
            <input className="input" disabled={skipInstance} value={instanceTitle} onChange={(event) => { setCurrentFlowCandidateIds([]); setInstanceTitle(event.target.value); }} placeholder="티켓에 적힌 제목" />
          </label>
          <label className="field">
            <span className="field-label">관람 날짜와 시간</span>
            <input className="input" type="datetime-local" disabled={skipInstance} value={instanceAt} onChange={(event) => { setCurrentFlowCandidateIds([]); setInstanceAt(event.target.value); }} />
          </label>
          <label className="candidate">
            <span><strong>회차를 모르겠어요</strong><br /><span className="micro">무대 구성 없이도 계속 비교할 수 있어요.</span></span>
            <input type="checkbox" checked={skipInstance} onChange={(event) => { setCurrentFlowCandidateIds([]); setSkipInstance(event.target.checked); }} />
          </label>
        </div>
      ) : null}

      {preview && venue && space && seatLayout ? <SeatPreviewSheet key={`${preview.section}:${preview.row}:${preview.number}`} candidate={{candidateId: 'preview', categoryId: venue.categoryId, venueId:venue.id, venueName:venue.name, spaceId:space.id, spaceName:space.name, layoutVersionId:seatLayout.layoutVersionId, instanceId:null, instanceLabel:null, sectionCode:preview.section, rowLabel:preview.row, seatNumber:preview.number, expectedPrice:null}} variant={import.meta.env.DEV && params.get('previewVariant') === 'tabs' ? 'tabs' : 'overview'} message={previewError} onClose={()=>setPreview(null)} onConfirm={()=>addSeat(true)}/>:null}
      {sectionPreview && seatLayout ? <BottomSheet title={sectionPreview.label} onClose={()=>setSectionPreview(null)}><ul>{seatFacts(seatLayout,sectionPreview.id,'',0).lines.map((line)=><li key={line}>{line}</li>)}</ul><p>{sectionPreview.rows?.length ? `${sectionPreview.rows.length}개 열이 있어요.`:'이 구역은 공식 자료의 구역까지만 확인했어요.'}</p><button className="button button-primary" type="button" onClick={()=>setSectionPreview(null)}>이 구역에서 좌석 고르기</button></BottomSheet>:null}
      {step === 5 ? (
        <div className="stack-lg">
          <div className="coordinate-entry">
            <div>
              <p className="eyebrow">티켓에 적힌 좌석</p>
              <h2>티켓에 적힌 좌석을 넣어요.</h2>
            </div>
          <div className="coordinate-grid">
            <label className="field"><span className="field-label">구역</span><input className="input" value={sectionCode} onChange={(event) => { setSectionCode(event.target.value); setSelectedFromMap(false); }} placeholder="1층 A구역" /></label>
            <label className="field"><span className="field-label">열</span><input className="input" value={rowLabel} onChange={(event) => { setRowLabel(event.target.value); setSelectedFromMap(false); }} placeholder="16" /></label>
            <label className="field"><span className="field-label">번호</span><input className="input" inputMode="numeric" value={seatNumber} onChange={(event) => { setSeatNumber(event.target.value.replace(/\D/g, '')); setSelectedFromMap(false); }} placeholder="32" /></label>
          </div>
          </div>
          <div className="quick-seat-actions">
            <button className="button button-primary" type="button" onClick={() => addSeat(true)}>{recordIntent ? '이 자리 기억 남기기' : '이 자리 확인하기'}</button>
            <button className="button button-secondary" type="button" onClick={() => addSeat()}>비교 후보로 담기</button>
          </div>
          <p className="micro">층·열·번호로 먼저 확인해요. 회차와 무대 구성은 아직 확인되지 않았어요.</p>
          <details className="card disclosure-card">
            <summary>회차·예상 금액도 남기기 (선택)</summary>
            <div className="stack disclosure-body">
              <label className="field"><span className="field-label">공연명이나 영화명</span><input className="input" value={instanceTitle} onChange={(event) => { setCurrentFlowCandidateIds([]); setInstanceTitle(event.target.value); setSkipInstance(false); }} placeholder="티켓에 적힌 제목" /></label>
              <label className="field"><span className="field-label">관람 날짜와 시간</span><input className="input" type="datetime-local" value={instanceAt} onChange={(event) => { setCurrentFlowCandidateIds([]); setInstanceAt(event.target.value); setSkipInstance(false); }} /></label>
              <label className="field"><span className="field-label">예상 결제 금액 (선택)</span><input className="input" inputMode="numeric" value={expectedPrice} onChange={(event) => setExpectedPrice(event.target.value.replace(/\D/g, ''))} placeholder="수수료 포함 예상 금액" /></label>
              <p className="micro">입력한 회차는 내 메모예요. 공식 배치가 확인됐다는 뜻은 아니에요.</p>
            </div>
          </details>
          <details open className="card disclosure-card">
            <summary>공식 배치에서 좌석 찾아보기</summary>
            <div className="disclosure-body">
          {seatLayout ? (
            <InteractiveSeatMap
              key={`${seatLayout.venueId}:${seatLayout.spaceId}`}
              layout={seatLayout}
              defaultEasy={state.fitProfile.mobilityNeed === true}
              memories={state.reviews.flatMap((review) => {
                const seat = reviewSeat(review, state);
                return seat && seat.venueId === venue?.id && seat.spaceId === space?.id
                  ? [{ ...seat, score: review.scores.시야 }] : [];
              })}
              onPreviewSection={(section)=>setSectionPreview(section)}
              selection={selectedFromMap && seatNumber ? { sectionCode, rowLabel, seatNumber: Number(seatNumber) } : null}
              onSelectSeat={(selection) => {
                setMessage(''); setPreviewError(''); setPreview({section: selection.sectionCode, row: selection.rowLabel, number: selection.seatNumber});
                setSectionCode(selection.sectionCode);
                setRowLabel(selection.rowLabel);
                setSeatNumber(String(selection.seatNumber));
                setSelectedFromMap(true);
                setMessage(`${selection.sectionCode} ${selection.rowLabel}열 ${selection.seatNumber}번을 골랐어요.`);
              }}
              onSelectSection={(nextSection) => {
                setSectionCode(nextSection);
                setRowLabel('');
                setSeatNumber('');
                setSelectedFromMap(false);
                setMessage(`${nextSection}을 골랐어요. 티켓의 열과 번호를 이어서 넣어 주세요.`);
              }}
            />
          ) : (
            <div className="notice"><span className="notice-mark">!</span><span>이 홀의 검증된 배치가 아직 없어요. 실제처럼 보이는 가짜 좌석도를 만들지 않고 티켓에 적힌 좌석를 직접 받아요.</span></div>
          )}
            </div>
          </details>
          {message ? <p role="status" className="muted">{message}</p> : null}
          <CandidateList candidates={currentFlowCandidates} onRemove={removeCandidate} label="이번에 담은 좌석" />
          {previousCandidates.length ? (
            <div className="stack-lg">
              <h3>이전에 담은 후보</h3>
              <p className="micro">이번에 고른 좌석과 구분해 보여줘요. 예전 후보는 아래에서 따로 확인할 수 있어요.</p>
              <CandidateList candidates={previousCandidates} onRemove={removeCandidate} label="이전에 담은 후보" />
              <Link className="button" to="/compare" state={{ candidateIds: previousCandidates.map((candidate) => candidate.candidateId) }}>기존 후보 확인하기</Link>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="wizard-nav">
        <button
          className="button"
          type="button"
          disabled={!directVenueEntry && step === 1}
          onClick={() => {
            if (directVenueEntry && step === 3) navigate('/explore');
            else setStep(step === 5 ? 3 : Math.max(1, step - 1) as Step);
          }}
        >
          {directVenueEntry && step === 3 ? '장소 다시 고르기' : '이전 단계'}
        </button>
        <button className={step === 2 && venues.length === 0 ? 'button' : 'button button-primary'} type="button" disabled={!canContinue} onClick={next}>
          {step === 5 ? (currentFlowCandidates.length === 0 ? '좌석을 먼저 담아 주세요' : currentFlowCandidates.length === 1 ? '이 자리 확인하기' : `이번에 담은 ${currentFlowCandidates.length}자리 비교하기`) : '다음'}
        </button>
      </div>
    </section>
  );
}

function CandidateList({ candidates, onRemove, label }: { candidates: SeatCandidate[]; onRemove(id: string): void; label: string }) {
  if (!candidates.length) return <p className="muted">아직 담은 좌석이 없어요.</p>;
  return (
    <section className="candidate-list" aria-label={label}>
      {candidates.map((candidate, index) => (
        <div className="candidate" key={candidate.candidateId}>
          <span className="inline"><span className="candidate-index">{String.fromCharCode(65 + index)}</span><span><strong>{candidate.sectionCode} {candidate.rowLabel}열 {candidate.seatNumber}번</strong><br /><span className="micro">{candidate.venueName} · {candidate.spaceName}</span></span></span>
          <button className="button button-ghost button-danger" type="button" onClick={() => onRemove(candidate.candidateId)} aria-label={`${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번 후보에서 빼기`}>빼기</button>
        </div>
      ))}
    </section>
  );
}

const stepTitles: Record<Step, string> = {
  1: '어떤 자리를 보고 있어요?',
  2: '어디서 보세요?',
  3: '어느 홀·상영관이에요?',
  4: '어떤 공연·회차예요?',
  5: '어느 자리를 확인할까요?',
};
